import {
  PROPOSED_SCHEDULE_MARKER,
  type ProposedSchedule,
} from '@app/+authenticated/+schedule/shared/auto-schedule/proposed-shift.adapter';
import type { RosterPayload } from '@app/reducers/orm/schedule/schedule.model';
import { toISODateString, type PlainDate } from '@shiftbase-com/ts-shared/utilities';
import { addDays, eachDayOfInterval, getDay, parseISO } from 'date-fns';

import type { DraftSchedulePeriod, ReconciledEntry, Weekday } from './onboarding-draft-schedule.models';

// getDay() returns 0 for Sunday … 6 for Saturday.
const WEEKDAY_BY_DATE_INDEX: Weekday[] = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];

/** Roster payload shape accepted by `ScheduleService.addBatch` for a recurring weekly shift. */
export interface RosterDraftPayload extends RosterPayload {
  id: string;
  date: string;
  department_id: string;
  team_id: string;
  shift_id: string;
  user_id: string;
  starttime: string;
  endtime: string;
  hide_end_time: boolean;
  break: string;
  paid_break: string;
  description: string;
  recurring: boolean;
  interval: string;
  repeat_until: string;
  mo: boolean;
  tu: boolean;
  we: boolean;
  th: boolean;
  fr: boolean;
  sa: boolean;
  su: boolean;
}

function datesInPeriodForDays(period: DraftSchedulePeriod, days: Weekday[]): PlainDate[] {
  const flags = new Set(days);
  return eachDayOfInterval({ start: parseISO(period.minDate), end: parseISO(period.maxDate) })
    .filter((date) => flags.has(WEEKDAY_BY_DATE_INDEX[getDay(date)]))
    .map((date) => toISODateString(date));
}

/**
 * Build the non-persisted draft overlay: concrete dated occurrences for every
 * (entry × matching weekday) that falls inside the currently viewed week. The
 * grid's optimized store filters proposals to the visible period, so the dates
 * must land in `[minDate, maxDate]`.
 */
export function buildProposedSchedules(entries: ReconciledEntry[], period: DraftSchedulePeriod): ProposedSchedule[] {
  const proposed: ProposedSchedule[] = [];

  entries.forEach((entry, entryIndex) => {
    for (const date of datesInPeriodForDays(period, entry.days)) {
      const occurrenceId = `${PROPOSED_SCHEDULE_MARKER}:${entryIndex}:${date}`;
      proposed.push({
        id: occurrenceId,
        occurrence_id: occurrenceId,
        user_id: entry.userId,
        team_id: entry.teamId,
        shift_id: entry.shiftId,
        department_id: entry.departmentId,
        date,
        starttime: entry.startTime,
        endtime: entry.endTime,
        hide_end_time: false,
        break: '0',
        paid_break: '0',
        description: '',
        recurring: false,
        repeat_until: '',
        interval: '',
        total: '0',
        salary: '0',
        coc: '0',
        wage: '0',
        is_task: false,
        mo: false,
        tu: false,
        we: false,
        th: false,
        fr: false,
        sa: false,
        su: false,
        [PROPOSED_SCHEDULE_MARKER]: true,
      });
    }
  });

  return proposed;
}

/**
 * First occurrence of any of `days` in the week starting at `period.minDate`.
 * A 7-day window always contains every weekday, so a recurring entry always gets
 * a start date — independent of how narrow the visible period is (a day/month
 * view must not silently drop entries whose weekday falls outside it).
 */
function firstRecurringDate(period: DraftSchedulePeriod, days: Weekday[]): string | null {
  if (days.length === 0) {
    return null;
  }

  const flags = new Set(days);
  const start = parseISO(period.minDate);

  for (let offset = 0; offset < 7; offset++) {
    const date = addDays(start, offset);
    if (flags.has(WEEKDAY_BY_DATE_INDEX[getDay(date)])) {
      return toISODateString(date);
    }
  }

  return null;
}

/**
 * Build one recurring weekly roster per reconciled entry for persistence on
 * accept. The roster starts on the first matching weekday in the week containing
 * `period.minDate` and repeats indefinitely on the entry's day flags — so every
 * reconciled entry produces exactly one payload, even when its weekday is not in
 * the currently-viewed window.
 */
export function buildRosterPayloads(entries: ReconciledEntry[], period: DraftSchedulePeriod): RosterDraftPayload[] {
  return entries.reduce<RosterDraftPayload[]>((payloads, entry) => {
    const firstDate = firstRecurringDate(period, entry.days);
    if (!firstDate) {
      return payloads;
    }

    payloads.push({
      id: '',
      date: firstDate,
      department_id: entry.departmentId,
      team_id: entry.teamId,
      shift_id: entry.shiftId,
      user_id: entry.userId,
      starttime: entry.startTime,
      endtime: entry.endTime,
      hide_end_time: false,
      break: '0',
      paid_break: '0',
      description: '',
      recurring: true,
      interval: '1',
      repeat_until: '',
      mo: entry.days.includes('mo'),
      tu: entry.days.includes('tu'),
      we: entry.days.includes('we'),
      th: entry.days.includes('th'),
      fr: entry.days.includes('fr'),
      sa: entry.days.includes('sa'),
      su: entry.days.includes('su'),
    });

    return payloads;
  }, []);
}
